import React from 'react';
import { SxProps } from '@mui/material';
import { PounceStyleProps } from '../../utils';
import {
  STATIC_CHIP_HEIGHT_PX,
  STATIC_CHIP_PADDING_X_PX,
  STATIC_CHIP_GAP_PX,
  STATIC_CHIP_ICON_PX,
  StaticChipType,
  StaticChipVariant,
  StaticChipSize,
} from './constants';
import { BaseChip } from './BaseChip';
import { ChipLabel } from './ChipLabel';
import { isComposedChildren } from './primitives';

export interface StaticChipProps extends PounceStyleProps {
  /**
   * Visual weight. Mirrors Figma `Type`.
   * @default 'filled'
   */
  type?: StaticChipType;
  /**
   * Semantic color. Mirrors Figma `Variant`.
   * @default 'primary'
   */
  variant?: StaticChipVariant;
  /**
   * Pixel-height tier. Mirrors Figma `Size`.
   * @default 'lg'
   */
  size?: StaticChipSize;
  /**
   * Applies a visual disabled effect (opacity 0.4, `cursor:not-allowed`,
   * `pointer-events:none`). Read-only signal — `StaticChip` is
   * non-interactive by design.
   */
  disabled?: boolean;
  /**
   * Optional leading icon. **Simple-mode only** — ignored when `children`
   * contains chip primitives (composed mode).
   */
  leadIcon?: React.ReactElement;
  /**
   * Optional trailing icon. **Simple-mode only** — ignored when `children`
   * contains chip primitives (composed mode).
   */
  trailIcon?: React.ReactElement;
  /**
   * In **simple mode** (most callers): plain text or any non-primitive
   * `ReactNode` — auto-wrapped in `<ChipLabel>`.
   *
   * In **composed mode**: include one or more chip primitives
   * (`<ChipAvatar>`, `<ChipLabel>`, `<ChipField>`) and the chip yields layout
   * to children. `leadIcon` / `trailIcon` are ignored in this mode.
   */
  children: React.ReactNode;
  sx?: SxProps;
}

/**
 * A non-interactive, pill-shaped chip used to display read-only labels,
 * statuses, severities, or metadata inline with content.
 *
 * `StaticChip` cannot be clicked or focused. Use `InteractiveChip` for
 * clickable / removable / selectable behavior.
 *
 * Figma: `📄 Chip Static — Documentation`.
 *
 * **Anatomy (simple mode):** `leadIcon` prop + `children` (label text) +
 * `trailIcon` prop.
 *
 * **Composed mode:** pass chip primitives in `children` to own the layout.
 *
 * **Type × variant × size matrix:** 3 × 9 × 3 = 81 visual permutations.
 *
 * **Accessibility posture:**
 * - Root is a `<div>` rendered with `tabIndex={-1}` and no role.
 * - When `disabled`, `aria-disabled="true"` is set.
 * - Color is never the sole carrier of meaning — pair severity-coded
 *   variants with a glyph or an explicit text label.
 *
 * @example Simple severity tag
 * ```tsx
 * <StaticChip variant="error" size="sm">CRITICAL</StaticChip>
 * ```
 *
 * @example Severity tag with a lead icon
 * ```tsx
 * <StaticChip variant="high-alert" leadIcon={<Icon type="alert-triangle" />}>HIGH</StaticChip>
 * ```
 *
 * @example Composed — entity-with-avatar pattern
 * ```tsx
 * <StaticChip variant="primary">
 *   <ChipAvatar src={user.avatarUrl} />
 *   <ChipLabel>{user.name}</ChipLabel>
 * </StaticChip>
 * ```
 */
export const StaticChip = React.forwardRef<HTMLDivElement, StaticChipProps>(function StaticChip(
  {
    type = 'filled',
    variant = 'primary',
    size = 'lg',
    disabled = false,
    leadIcon,
    trailIcon,
    children,
    sx,
    ...rest
  },
  ref,
) {
  const composed = isComposedChildren(children);
  // Lead, label, and trail share `.MuiChip-label`'s single flex row for
  // uniform spacing — see BaseChip for the rationale on skipping MUI's
  // `.MuiChip-icon` slot.
  const label = composed ? (
    children
  ) : (
    <>
      {leadIcon}
      <ChipLabel>{children}</ChipLabel>
      {trailIcon}
    </>
  );

  return (
    <BaseChip
      ref={ref}
      muiVariant={`static-${type}-${variant}`}
      muiSize={size}
      geometry={{
        height: STATIC_CHIP_HEIGHT_PX[size],
        paddingX: STATIC_CHIP_PADDING_X_PX[size],
        gap: STATIC_CHIP_GAP_PX,
        iconPx: STATIC_CHIP_ICON_PX[size],
      }}
      label={label}
      disabled={disabled}
      tabIndex={-1}
      rootDataAttribute="data-pv2-chip-static"
      sx={sx}
      {...rest}
    />
  );
});
